/**
 * =============================================================================
 * Página: ReferralDashboard
 * Versão: 4.0.0
 *
 * Descrição:
 *   Página principal do Dashboard de Indicação. Segue o mesmo padrão visual
 *   do Dashboard CRM:
 *     - Título + refresh + filtros (mesma linha do header).
 *     - Abas de dashboards (sempre visíveis, mesmo com 1 dashboard).
 *     - Renderização dinâmica via `DashboardRenderer`.
 *
 *   O filtro de período (7/30/90 dias) saiu de dentro do componente
 *   `ReferralOverviewDashboard` e passou a viver no header da página,
 *   sendo propagado via `referralFilters` ao DashboardRenderer.
 *
 * RELEASE: 2026-05-03 v4.0.0 - Padroniza layout com Dashboard CRM e move
 *   filtro de período para o header.
 * =============================================================================
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Megaphone,
  RefreshCw,
  LayoutDashboard,
  Clock,
} from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
import {
  DashboardTabs,
  DashboardRenderer,
} from '@/components/crm/dashboard';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';
import { cn } from '@/lib/utils';

/** Períodos pré-definidos (em dias) — equivalente ao do Dashboard CRM. */
const PERIOD_OPTIONS = [
  { value: '7', label: '7 dias' },
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
] as const;

type PeriodValue = (typeof PERIOD_OPTIONS)[number]['value'];

export default function ReferralDashboard() {
  const queryClient = useQueryClient();

  /** Hook para refresh com timestamp visual */
  const { isRefreshing, formattedTime, triggerRefresh } =
    useRefreshWithTimestamp();

  /** Período selecionado no header (default: 30 dias). */
  const [period, setPeriod] = useState<PeriodValue>('30');

  /** Range de datas derivado da seleção do usuário. */
  const referralFilters = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - Number(period));
    return { startDate: start, endDate: end };
  }, [period]);

  /** Dashboards do módulo Indicação disponíveis para o usuário */
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading,
  } = useModuleDashboards('referral');

  /** Refresh dos dados de KPIs */
  const handleRefresh = useCallback(async () => {
    await triggerRefresh(async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['referral-dashboard-kpis'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['referral-affiliates-paginated'],
        }),
      ]);
    });
  }, [queryClient, triggerRefresh]);

  // Loading inicial
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sem dashboards disponíveis (sem permissão ou módulo recém-ativado)
  if (dashboards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <LayoutDashboard className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-2xl font-bold text-foreground">
          Dashboard de Indicação
        </h1>
        <p className="text-muted-foreground text-center max-w-md">
          Nenhum dashboard disponível. Verifique suas permissões de acesso em
          Permissões Dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho: Título + Refresh | Filtros (período) */}
      <div className="flex items-center gap-2">
        {/* Esquerda: Título + Refresh (não encolhe) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Megaphone className="h-5 w-5 text-primary" />
          <h1 className="text-lg md:text-2xl font-bold tracking-tight text-foreground">
            Dashboard de Indicação
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
            />
          </Button>
          <span className="text-[10px] hidden md:inline-flex items-center gap-1 text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            {formattedTime}
          </span>
        </div>

        {/* Separador visual */}
        <span className="text-muted-foreground/30 shrink-0">|</span>

        {/* Centro: Filtro de período (em pill, mesmo padrão do CRM) */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
          <Tabs
            value={period}
            onValueChange={(v) => setPeriod(v as PeriodValue)}
          >
            <TabsList className="h-[22px] p-0.5">
              {PERIOD_OPTIONS.map((opt) => (
                <TabsTrigger
                  key={opt.value}
                  value={opt.value}
                  className="h-[18px] px-2 text-[11px]"
                >
                  {opt.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Direita: indicador de uso/limite + créditos do plano de Indicação */}
        <ReferralUsageInline className="shrink-0 hidden md:inline-flex" />
      </div>

      {/* Abas de dashboards — sempre visíveis (mesmo com 1 dashboard) */}
      <DashboardTabs
        dashboards={dashboards}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={selectDashboard}
        alwaysShow
      />

      {/* Renderização dinâmica do dashboard selecionado */}
      {selectedDashboard && (
        <DashboardRenderer
          componentKey={selectedDashboard.component_key}
          dashboardId={selectedDashboard.id}
          referralFilters={referralFilters}
        />
      )}
    </div>
  );
}
